import React from 'react';

/**
 * Error boundary around a single game board.
 *
 * Without one, a board that throws while rendering unmounts everything up to
 * the app-level boundary in main.tsx: the player loses the room shell, the
 * roster and the chat, and gets a bare "Something broke. Reload to retry." —
 * for what may be one bad frame of game state. Here the blast radius is the
 * board itself. The room stays live, the error is reported, and "Try again"
 * re-mounts just the board, which is enough whenever the next state snapshot
 * from the server is well-formed.
 */
interface Props {
  children: React.ReactNode;
  /** Shown in the fallback so the player knows which game failed. */
  gameName?: string;
  /** Remounting is pointless if the board will re-render the same bad state;
   *  bump this (e.g. with the state sequence number) to retry automatically. */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

export class GameBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // A fresh state snapshot arrived — give the board another chance.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[game] board crashed', this.props.gameName ?? '', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/5 px-6 py-8 text-center"
      >
        <p className="text-sm font-bold uppercase tracking-widest text-red-400">
          {this.props.gameName ? `${this.props.gameName} hit an error` : 'This game hit an error'}
        </p>
        <p className="max-w-xs text-xs text-gray-500 dark:text-gray-400">
          The rest of the room is fine — the board stopped drawing. Try again, or leave and rejoin
          if it keeps happening.
        </p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="min-h-9 rounded-xl bg-gray-800 px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-200 transition-colors hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
        >
          Try again
        </button>
      </div>
    );
  }
}
